import * as React from 'react';
import { useCallback, useRef } from 'react';
import CloseIcon from '../../images/modals/close-x.svg';
import './Modal.scss';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { CSSTransition } from 'react-transition-group';
import { DrawerComponentProps } from '../drawer/drawer';
import { ShowBillingModal } from '../../helpers/helpers';
import { local } from '../../helpers/storageFactory';

type ModalProps = {
    isOpen: boolean;
    toggle?: (e) => void;
    fullScreen?: boolean;
    children: React.ReactNode;
    id: string;
    hideCancel?: boolean;
    videoOverride?: boolean;
    setLocalStorage?: boolean;
} & Partial<DefaultProps>;
type DefaultProps = Readonly<typeof defaultProps>;

const defaultProps = {
    fullScreen: false,
    hideCancel: false,
};

export const Modal: React.FunctionComponent<ModalProps & DrawerComponentProps> = ({
    isOpen,
    fullScreen,
    id,
    toggle,
    children,
    hideCancel,
    videoOverride,
    setLocalStorage,
}: ModalProps & DrawerComponentProps) => {
    const cssNameSpace = 'tsx-modal';

    const node = useRef();

    const close = useCallback(
        (open, e): void => {
            if (open && toggle) {
                toggle(e);
                if (setLocalStorage) {
                    local.setItem(id, 'true');
                }
            }
        },
        [id, setLocalStorage, toggle]
    );

    const closeCallback = useCallback(
        (e) => {
            close(isOpen, e);
        },
        [isOpen, close]
    );

    useOnClickOutside(node, closeCallback);

    const topLevelProps = {
        className: `${cssNameSpace} ${fullScreen ? `${cssNameSpace}--fullscreen` : ''}  ${
            id === ShowBillingModal ? 'billing__modal--top-level' : ''
        }`,
        ref: null,
        id,
    };

    let classNameToUse = `${cssNameSpace}__content-wrap`;
    if (fullScreen) {
        classNameToUse = `${cssNameSpace}__content-wrap--fullscreen`;
    }
    if (videoOverride) {
        classNameToUse = `${cssNameSpace}__content-wrap--video`;
    }

    const inOverlayProps = {
        className: `${cssNameSpace}__content-wrap ${
            id === ShowBillingModal ? 'billing__modal' : ''
        } ${id === 'leadsPlusIntro' ? 'leads-plus-intro-modal__modal' : ''} ${classNameToUse}`,
        ref: null,
    };

    if (fullScreen) {
        topLevelProps.ref = node;
    } else {
        inOverlayProps.ref = node;
    }

    return (
        <CSSTransition
            in={isOpen}
            classNames={`${cssNameSpace}-`}
            timeout={{
                appear: 0,
                enter: 0,
                exit: 300,
            }}
            mountOnEnter
            unmountOnExit
        >
            <div {...topLevelProps}>
                <div
                    className={`${cssNameSpace}__wrap ${
                        fullScreen ? `${cssNameSpace}__wrap--fullscreen` : ''
                    }`}
                >
                    {!hideCancel && (
                        <div
                            onClick={(e) => close(isOpen, e)}
                            className={`${cssNameSpace}__close-button`}
                        >
                            Cancel <CloseIcon className={`${cssNameSpace}__close-icon`} />
                        </div>
                    )}
                    <div {...inOverlayProps}>
                        {/* This renders any child DOM nodes */}
                        <div className={`${cssNameSpace}__content-wrap-wrap`}>{children}</div>
                    </div>
                </div>
            </div>
        </CSSTransition>
    );
};
